import { useRef, useState } from "react";
import { bool, number, object, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { BreakdownTableDepths } from "@repo/utils";
import { addLibraryElementQuery } from "@query";
import { useStores } from "@hooks";
import {
  LabelSelect,
  useEstimateStructure,
  useLibraryPusher,
  useEstimateTableSettings, ItemBuffers, useEstimateEditorSettings
} from "@tools";
import { Alert, MenuButton, PopMenu } from "@components";
import { CircularProgress, IconButton } from "@material-ui/core";
import { UserStoryGenerator } from "./UserStoryGenerator";
import { SelectRowType } from "./SelectRowTypes/SelectRowType";
import { SelectSectionType } from "./SelectSectionType";
import { Label, Menu } from "@assets";
import { Add, FileCopy } from "@material-ui/icons";
import classnames from "classnames";

export const BreakdownRowActionMenu = ({
  element,
  className,
  tableDepth,
  showBuffers,
}) => {
  const { userStore } = useStores();
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { isLibrary } = useEstimateEditorSettings();
  const { showLabelsColumn, setShowLabelsColumn, ColumnSettings } = useEstimateTableSettings();
  
  const [ showMenu, setShowMenu ] = useState(false);
  const [ addedLabels, setAddedLabels ] = useState(false);
  const [ showLabelsDialog, setShowLabelsDialog ] = useState(false);
  
  const anchor = useRef(null);
  const labelsAnchor = useRef(null);
  
  const { showBreakdownLabelsColumn } = structure.settings;
  const {
    isLoading,
    libraryPushAlertStatus,
    libraryPushSuccess,
    addToLibrary,
    closeAlert,
    closeSuccessAlert,
  } = useLibraryPusher({ element, tableDepth });
  
  const handleDuplicate = async () => {
    let name = element.name || t("common.unnamed");
    
    if( isLibrary ) {
      name = element.getDuplicateName(name);
      const id = await addLibraryElementQuery(
        element.lvl,
        name,
        element.content,
        element.parentOrder + 1,
        element.lvl > BreakdownTableDepths.MODULE ? element.parentId : undefined
      );
      element.duplicateSelf(name, id);
    } else
      element.duplicateSelf(
        element.getDuplicateName(name)
      );
  };
  
  const handleClickAway = () => {
    if(addedLabels && structure.addedLabels?.length && !showLabelsColumn)
      setShowLabelsColumn();
    setShowMenu(false);
    setShowLabelsDialog(false);
  }
  
  const loading = isLoading && !showMenu;
  
  return <>
    <IconButton
      ref={anchor}
      onClick={() => setShowMenu(!showMenu)}
      className={classnames("button-menu noDrag hoverIcon opq", className)}
      color="primary"
      size="small"
      tabIndex={-1}
    >
      { loading
        ? <CircularProgress size={18} />
        : <Menu />
      }
    </IconButton>
    <PopMenu
      anchor={anchor.current}
      show={showMenu}
      onClickAway={handleClickAway}
    >
      {
        !isLibrary &&
        <ColumnSettings />
      }
      {
        !isLibrary && tableDepth === BreakdownTableDepths.SECTION &&
        <SelectSectionType section={element} />
      }
      {
        !isLibrary &&
        <SelectRowType element={element} />
      }
      {
        !isLibrary && tableDepth > BreakdownTableDepths.SECTION &&
        <MenuButton
          icon={isLoading ? <CircularProgress size={18} /> : <Add color="primary" />}
          onClick={addToLibrary}
        >
          {t("views.editor.add_to_library")}
        </MenuButton>
      }
      {
        showBuffers && Boolean(element.name) &&
        <ItemBuffers element={element} useMenuButton />
      }
      {
        !isLibrary && (!showBreakdownLabelsColumn || showLabelsDialog) &&
        <MenuButton
          ref={labelsAnchor}
          icon={<Label color="primary" />}
          onClick={() => setShowLabelsDialog(true)}
        >
          {t("views.editor.labels.add_label")}
        </MenuButton>
      }
      <MenuButton
        icon={<FileCopy color="primary" />}
        onClick={handleDuplicate}
      >
        {t("views.editor.duplicate", {
          type: t("proposal_parts.module_levels.level_" + tableDepth),
        })}
      </MenuButton>
      <UserStoryGenerator element={element} menuAction />
      
      {
        showLabelsDialog &&
        <LabelSelect
          show={showLabelsDialog}
          element={element}
          anchorEl={labelsAnchor.current}
          onClose={() => setShowLabelsDialog(false)}
          onAddedLabels={setAddedLabels}
        />
      }
      <Alert
        isOpen={!!libraryPushAlertStatus}
        title={t(libraryPushAlertStatus)}
        acceptText={t("common.close")}
        onAccept={closeAlert}
      />
      <Alert
        isOpen={libraryPushSuccess}
        title={t(
          `alerts.library.added_${
            userStore.isManager ? "manager" : "pending"
          }`,
          { name: element.name }
        )}
        acceptText={t("common.close")}
        onAccept={closeSuccessAlert}
      />
    </PopMenu>
  </>
}

BreakdownRowActionMenu.propTypes = {
  element: object.isRequired,
  className: string,
  tableDepth: number,
  showBuffers: bool,
}